import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import { Settings, BarChart2 } from 'lucide-react';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';

/**
 * Composant pour afficher la ventilation des performances par score de qualité
 * Utilise l'endpoint /keywords existant et traite les données côté client
 */
const QualityScoreBreakdown = ({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  token,
  onQualityScoreFilter,
  selectedQualityScoreFilter,
  selectedMatchTypeFilter,
  selectedCampaignFilter,
  selectedCpcTierFilter,
  userRole = 'USER'
}) => {
  const [breakdownData, setBreakdownData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('keywords_quality_score', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Fonction pour charger les données des mots-clés et calculer la ventilation
  const loadQualityScoreBreakdown = async () => {
    if (!selectedAccount || !token) return;
    
    try {
      setLoading(true);
      setError(null);
      const requestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod
      };

      if (selectedPeriod === 'CUSTOM') {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      const response = await axios.post(
        `${config.API_URL}/api/dashboard/keywords`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const keywordsData = response.data.data[0];
        
        if (keywordsData.keywords && keywordsData.keywords.length > 0) {
          // Calculer la ventilation par score de qualité
          const breakdown = calculateQualityScoreBreakdown(keywordsData.keywords);
          setBreakdownData(breakdown);
        } else {
          setBreakdownData(null);
        }
      } else {
        setBreakdownData(null);
      }
    } catch (err) {
      console.error('❌ [QUALITY-SCORE-BREAKDOWN] Erreur:', err);
      setError('Erreur lors du chargement de la ventilation');
      setBreakdownData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour calculer la ventilation par score de qualité
  const calculateQualityScoreBreakdown = (keywords) => {
    // Filtrer par campagne d'abord si un filtre est actif
    let filteredKeywords = keywords;
    if (selectedCampaignFilter !== null && selectedCampaignFilter !== undefined) {
      filteredKeywords = keywords.filter(keyword => {
        return keyword.campaignName === selectedCampaignFilter.id;
      });
    }
    
    // Ensuite filtrer par type de correspondance si un filtre est actif
    if (selectedMatchTypeFilter !== null && selectedMatchTypeFilter !== undefined) {
      filteredKeywords = filteredKeywords.filter(keyword => {
        return keyword.matchType === selectedMatchTypeFilter;
      });
    }
    
    // Enfin filtrer par palier CPC si un filtre est actif
    if (selectedCpcTierFilter !== null && selectedCpcTierFilter !== undefined) {
      filteredKeywords = filteredKeywords.filter(keyword => {
        const cpc = Number(keyword.cpc) || 0;
        return cpc >= selectedCpcTierFilter.min && cpc <= selectedCpcTierFilter.max;
      });
    }

    const qualityScoreGroups = {};
    let totalKeywords = 0;

    filteredKeywords.forEach(keyword => {
      const score = keyword.qualityScore || 0;
      const scoreKey = score === 0 ? 'Non défini' : score.toString();
      
      if (!qualityScoreGroups[scoreKey]) {
        qualityScoreGroups[scoreKey] = {
          score: score,
          keywordCount: 0,
          totalImpressions: 0,
          totalClicks: 0,
          totalCost: 0,
          totalConversions: 0,
          totalConversionsValue: 0
        };
      }
      
      const group = qualityScoreGroups[scoreKey];
      group.keywordCount++;
      group.totalImpressions += keyword.impressions || 0;
      group.totalClicks += keyword.clicks || 0;
      group.totalCost += keyword.cost || 0;
      group.totalConversions += keyword.conversions || 0;
      group.totalConversionsValue += keyword.conversionsValue || 0;
      
      totalKeywords++;
    });

    // Convertir en array et calculer les métriques
    const breakdownArray = Object.keys(qualityScoreGroups).map(scoreKey => {
      const data = qualityScoreGroups[scoreKey];
      
      const ctr = data.totalImpressions > 0 ? (data.totalClicks / data.totalImpressions * 100) : 0;
      const cpc = data.totalClicks > 0 ? (data.totalCost / data.totalClicks) : 0;
      const conversionRate = data.totalClicks > 0 ? (data.totalConversions / data.totalClicks * 100) : 0;
      const cpa = data.totalConversions > 0 ? (data.totalCost / data.totalConversions) : 0;
      const cos = data.totalConversionsValue > 0 ? (data.totalCost / data.totalConversionsValue * 100) : 0;
      
      return {
        qualityScore: data.score,
        qualityScoreLabel: scoreKey,
        keywordCount: data.keywordCount,
        keywordPercentage: Number(((data.keywordCount / totalKeywords) * 100).toFixed(1)),
        impressions: data.totalImpressions,
        clicks: data.totalClicks,
        cost: data.totalCost,
        conversions: data.totalConversions,
        conversionsValue: data.totalConversionsValue,
        ctr: ctr,
        cpc: cpc,
        conversionRate: conversionRate,
        cpa: cpa,
        cos: cos
      };
    });

    // Calculer les totaux sur les données filtrées
    const totals = filteredKeywords.reduce((acc, keyword) => ({
      keywordCount: acc.keywordCount + 1,
      impressions: acc.impressions + (keyword.impressions || 0),
      clicks: acc.clicks + (keyword.clicks || 0),
      cost: acc.cost + (keyword.cost || 0),
      conversions: acc.conversions + (keyword.conversions || 0),
      conversionsValue: acc.conversionsValue + (keyword.conversionsValue || 0)
    }), { keywordCount: 0, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
    totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
    totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
    totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
    totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;

    return {
      breakdown: breakdownArray,
      totals: totals,
      totalKeywords: totalKeywords
    };
  };

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Filtrer et trier les données de ventilation
  const filteredAndSortedBreakdown = React.useMemo(() => {
    if (!breakdownData?.breakdown) return [];
    
    let filtered = breakdownData.breakdown;
    
    // Filtrer par score de qualité si un filtre est actif
    if (selectedQualityScoreFilter !== null && selectedQualityScoreFilter !== undefined) {
      filtered = filtered.filter(row => row.qualityScore === selectedQualityScoreFilter);
    }
    
    return [...filtered].sort((a, b) => {
      // Cas spécial pour le score de qualité : "Non défini" toujours à la fin
      if (sortConfig.key === 'qualityScore') {
        if (a.qualityScore === 0 && b.qualityScore !== 0) return 1;
        if (b.qualityScore === 0 && a.qualityScore !== 0) return -1;
      }
      
      const aValue = a[sortConfig.key] ?? 0;
      const bValue = b[sortConfig.key] ?? 0;
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [breakdownData, sortConfig, selectedQualityScoreFilter]);

  // Charger les données quand les dépendances changent
  useEffect(() => {
    if (selectedAccount && selectedPeriod && isVisible) {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      loadQualityScoreBreakdown();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, isVisible, selectedMatchTypeFilter, selectedCampaignFilter, selectedCpcTierFilter]);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><BarChart2 size={16} />&nbsp;Mots-clés par Score de Qualité</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <button
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title="Masquer/Afficher la section"
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {!isVisible ? null : (
        <>
          {loading ? (
            <div className="loading-state" style={{ flexDirection: 'column' }}>
              <div className="loading-spinner"></div>
              Calcul de la ventilation...
            </div>
          ) : error ? (
            <div className="error-message">
              {error}
            </div>
          ) : !breakdownData || !breakdownData.breakdown || breakdownData.breakdown.length === 0 ? (
            <div className="empty-message">
              Aucune donnée disponible pour la ventilation par score de qualité
            </div>
          ) : (
            <>
              {/* Histogramme des scores de qualité */}
              <div className="quality-score-histogram" style={{
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <div className="histogram-container" style={{
                  display: 'flex',
                  alignItems: 'end',
                  height: '120px',
                  gap: '4px',
                  padding: '10px 0'
                }}>
                  {[...breakdownData.breakdown].sort((a, b) => {
                    // "Non défini" (score 0) toujours en dernier
                    if (a.qualityScore === 0 && b.qualityScore !== 0) return 1;
                    if (b.qualityScore === 0 && a.qualityScore !== 0) return -1;
                    // Sinon, ordre décroissant (10 à 1)
                    return b.qualityScore - a.qualityScore;
                  }).map((row, index) => {
                    const maxKeywords = Math.max(...breakdownData.breakdown.map(r => r.keywordCount));
                    const height = maxKeywords > 0 ? (row.keywordCount / maxKeywords) * 100 : 0;
                    const isSelected = selectedQualityScoreFilter === row.qualityScore;
                    const isOtherSelected = selectedQualityScoreFilter !== null && selectedQualityScoreFilter !== undefined && !isSelected;
                    
                    return (
                      <div key={`hist-${row.qualityScore}-${index}`} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        flex: '1',
                        minWidth: '40px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (onQualityScoreFilter) {
                          // Si on clique sur la barre déjà sélectionnée, on défiltre
                          if (selectedQualityScoreFilter === row.qualityScore) {
                            onQualityScoreFilter(null);
                          } else {
                            onQualityScoreFilter(row.qualityScore);
                          }
                        }
                      }}
                      title={`${row.keywordCount} mots-clés avec score ${row.qualityScoreLabel} (${row.keywordPercentage}%)`}
                      >
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '500',
                          marginBottom: '4px',
                          color: '#495057'
                        }}>
                          {formatNumber(row.keywordCount)}
                        </div>
                        <div style={{
                          width: '100%',
                          height: `${height}px`,
                          backgroundColor: isOtherSelected ? '#d6d6d6' : 
                            row.qualityScore === 0 ? '#6c757d' :
                            row.qualityScore >= 8 ? '#28a745' :
                            row.qualityScore >= 5 ? '#ffc107' : '#dc3545',
                          borderRadius: '4px 4px 0 0',
                          transition: 'all 0.2s ease',
                          opacity: isOtherSelected ? 0.5 : (isSelected ? 1 : 0.8),
                          transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                          border: 'none',
                          minHeight: height < 5 ? '5px' : 'auto',
                          boxShadow: isSelected ? '0 4px 8px rgba(0,91,179,0.3)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected && !isOtherSelected) {
                            e.target.style.opacity = '1';
                            e.target.style.transform = 'scale(1.02)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected && !isOtherSelected) {
                            e.target.style.opacity = '0.8';
                            e.target.style.transform = 'scale(1)';
                          }
                        }}
                        />
                        <div className={`quality-score quality-score-${row.qualityScore || 0}`} style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          marginTop: '4px',
                          padding: '2px 4px',
                          borderRadius: '3px',
                          minWidth: '24px',
                          textAlign: 'center'
                        }}>
                          {row.qualityScoreLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {breakdownData.breakdown.length > 0 && (
                  <div style={{
                    fontSize: '11px',
                    color: '#6c757d',
                    textAlign: 'center',
                    marginTop: '10px'
                  }}>
                    Cliquez sur une barre pour filtrer • Total: {formatNumber(breakdownData.totals.keywordCount)} mots-clés
                  </div>
                )}
              </div>
              
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('qualityScore')} className="sortable">
                        Score de Qualité {sortConfig.key === 'qualityScore' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('keywordCount')} className="sortable numeric">
                        Mots-clés {sortConfig.key === 'keywordCount' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {isComponentMetricSelected('impressions') && (
                        <th onClick={() => handleSort('impressions')} className="sortable numeric">
                          Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('ctr') && (
                        <th onClick={() => handleSort('ctr')} className="sortable numeric">
                          CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('clics') && (
                        <th onClick={() => handleSort('clicks')} className="sortable numeric">
                          Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cpc') && (
                        <th onClick={() => handleSort('cpc')} className="sortable numeric">
                          CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cout') && (
                        <th onClick={() => handleSort('cost')} className="sortable numeric">
                          Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('taux_conv') && (
                        <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                          Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('conv') && (
                        <th onClick={() => handleSort('conversions')} className="sortable numeric">
                          Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cpa') && (
                        <th onClick={() => handleSort('cpa')} className="sortable numeric">
                          CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('valeur_conv') && (
                        <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                          Valeur conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cos') && (
                        <th onClick={() => handleSort('cos')} className="sortable numeric">
                          COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedBreakdown.map((row, index) => {
                      const cosClass = getCosClassName(row.cos || 0, selectedAccount?.id || 'global');
                      
                      return (
                        <tr key={`quality-${row.qualityScore}-${index}`}>
                          <td>
                            <span 
                              className={`quality-score quality-score-${row.qualityScore || 0} quality-score-clickable`}
                              onClick={() => onQualityScoreFilter && onQualityScoreFilter(row.qualityScore)}
                              title={`Filtrer les mots-clés avec un score de qualité ${row.qualityScoreLabel}`}
                            >
                              {row.qualityScoreLabel}
                            </span>
                          </td>
                          <td className="numeric">{formatNumber(row.keywordCount)}</td>
                          {isComponentMetricSelected('impressions') && (
                            <td className="numeric">{formatNumber(row.impressions)}</td>
                          )}
                          {isComponentMetricSelected('ctr') && (
                            <td className="numeric">{formatNumber(row.ctr, 2)}%</td>
                          )}
                          {isComponentMetricSelected('clics') && (
                            <td className="numeric">{formatNumber(row.clicks)}</td>
                          )}
                          {isComponentMetricSelected('cpc') && (
                            <td className="numeric">€{formatNumber(row.cpc, 2)}</td>
                          )}
                          {isComponentMetricSelected('cout') && (
                            <td className="numeric">€{formatNumber(row.cost, 2)}</td>
                          )}
                          {isComponentMetricSelected('taux_conv') && (
                            <td className="numeric">{formatNumber(row.conversionRate, 2)}%</td>
                          )}
                          {isComponentMetricSelected('conv') && (
                            <td className="numeric">{formatNumber(row.conversions, 1)}</td>
                          )}
                          {isComponentMetricSelected('cpa') && (
                            <td className={`numeric ${getCpaClassName(row.cpa, selectedAccount?.id || 'global', 'keywords_quality_score')}`}>{row.cpa >= 0 ? `€${formatNumber(row.cpa, 2)}` : '-'}</td>
                          )}
                          {isComponentMetricSelected('valeur_conv') && (
                            <td className="numeric">€{formatNumber(row.conversionsValue, 2)}</td>
                          )}
                          {isComponentMetricSelected('cos') && (
                            <td className={`numeric ${getCosClassName(row.cos || 0, selectedAccount?.id || 'global', 'keywords_quality_score')}`}>{formatNumber(row.cos, 1)}%</td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  {!selectedQualityScoreFilter && (
                    <tfoot>
                      <tr className="totals-row">
                        <td><strong>TOTAL</strong></td>
                        <td className="numeric"><strong>{formatNumber(breakdownData.totals.keywordCount)}</strong></td>
                        {isComponentMetricSelected('impressions') && (
                          <td className="numeric"><strong>{formatNumber(breakdownData.totals.impressions)}</strong></td>
                        )}
                        {isComponentMetricSelected('ctr') && (
                          <td className="numeric"><strong>{formatNumber(breakdownData.totals.ctr, 2)}%</strong></td>
                        )}
                        {isComponentMetricSelected('clics') && (
                          <td className="numeric"><strong>{formatNumber(breakdownData.totals.clicks)}</strong></td>
                        )}
                        {isComponentMetricSelected('cpc') && (
                          <td className="numeric"><strong>€{formatNumber(breakdownData.totals.cpc, 2)}</strong></td>
                        )}
                        {isComponentMetricSelected('cout') && (
                          <td className="numeric"><strong>€{formatNumber(breakdownData.totals.cost, 2)}</strong></td>
                        )}
                        {isComponentMetricSelected('taux_conv') && (
                          <td className="numeric"><strong>{formatNumber(breakdownData.totals.conversionRate, 2)}%</strong></td>
                        )}
                        {isComponentMetricSelected('conv') && (
                          <td className="numeric"><strong>{formatNumber(breakdownData.totals.conversions, 1)}</strong></td>
                        )}
                        {isComponentMetricSelected('cpa') && (
                          <td className={`numeric ${getCpaClassName(breakdownData.totals.cpa, selectedAccount?.id || 'global', 'keywords_quality_score')}`}><strong>{breakdownData.totals.cpa >= 0 ? `€${formatNumber(breakdownData.totals.cpa, 2)}` : '-'}</strong></td>
                        )}
                        {isComponentMetricSelected('valeur_conv') && (
                          <td className="numeric"><strong>€{formatNumber(breakdownData.totals.conversionsValue, 2)}</strong></td>
                        )}
                        {isComponentMetricSelected('cos') && (
                          <td className={`numeric ${getCosClassName(breakdownData.totals.cos, selectedAccount?.id || 'global', 'keywords_quality_score')}`}>
                            <strong>{formatNumber(breakdownData.totals.cos, 1)}%</strong>
                          </td>
                        )}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="keywords_quality_score"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Score de Qualité"
      />
    </div>
  );
};

export default QualityScoreBreakdown;